import type { RunState } from '@crackpoint/shared/types';
import { strings } from '../../config';
import { useTheme } from '../ui/ThemeProvider';
import { Button } from '../ui/Button';

interface GameOverScreenProps {
  run: RunState;
  onRestart: () => void;
}

export function GameOverScreen({ run, onRestart }: GameOverScreenProps) {
  const { theme } = useTheme();

  return (
    <div
      className="game-over-screen"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.colours.background.primary,
        padding: theme.spacing.xl,
        fontFamily: theme.typography.fontFamily,
        overflow: 'auto',
      }}
    >
      <div
        className="game-over-screen__content"
        style={{
          maxWidth: '600px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing.xl,
          alignItems: 'center',
        }}
      >
        <div
          style={{
            fontSize: '4rem',
            marginBottom: theme.spacing.md,
          }}
        >
          {strings.gameOver.icon}
        </div>

        <h1
          style={{
            fontSize: theme.typography.fontSize.xxl,
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colours.accent.error,
            margin: 0,
            textAlign: 'center',
          }}
        >
          {strings.gameOver.title}
        </h1>

        <p
          style={{
            fontSize: theme.typography.fontSize.lg,
            color: theme.colours.text.secondary,
            textAlign: 'center',
            margin: 0,
          }}
        >
          {strings.gameOver.description}
        </p>

        <div
          className="game-over-screen__stats"
          style={{
            width: '100%',
            backgroundColor: theme.colours.background.elevated,
            borderRadius: theme.borders.radius.lg,
            padding: theme.spacing.lg,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing.md,
          }}
        >
          <h2
            style={{
              fontSize: theme.typography.fontSize.lg,
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.colours.text.primary,
              margin: 0,
              marginBottom: theme.spacing.sm,
            }}
          >
            {strings.gameOver.statsTitle}
          </h2>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: theme.typography.fontSize.md,
              color: theme.colours.text.primary,
            }}
          >
            <span>{strings.gameOver.roomsCleared}</span>
            <span style={{ fontWeight: theme.typography.fontWeight.semibold }}>{run.rooms_cleared}</span>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: theme.typography.fontSize.md,
              color: theme.colours.text.primary,
            }}
          >
            <span>{strings.gameOver.totalStars}</span>
            <span style={{ fontWeight: theme.typography.fontWeight.semibold }}>⭐ {run.total_stars}</span>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: theme.typography.fontSize.md,
              color: theme.colours.text.primary,
            }}
          >
            <span>{strings.gameOver.bestStreak}</span>
            <span style={{ fontWeight: theme.typography.fontWeight.semibold }}>
              {run.highest_streak > 0 ? `🔥 ${run.highest_streak}` : '0'}
            </span>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: theme.typography.fontSize.md,
              color: theme.colours.text.primary,
            }}
          >
            <span>{strings.gameOver.deepestRoom}</span>
            <span style={{ fontWeight: theme.typography.fontWeight.semibold }}>{run.current_room_number}</span>
          </div>
        </div>

        <Button variant="primary" size="lg" fullWidth onClick={onRestart}>
          {strings.gameOver.reconnectButton}
        </Button>

        <div
          style={{
            fontSize: theme.typography.fontSize.sm,
            color: theme.colours.text.tertiary,
          }}
        >
          {strings.gameOver.enterHint}
        </div>
      </div>
    </div>
  );
}
